/**
 * Unified layer recommendation logic
 * Used by both frontend and backend for consistent recommendations
 */

/**
 * Core layer recommendation logic based on temperature
 * @param {number} temperature - The temperature in Celsius (apparent or direct)
 * @param {number} precipitation - Precipitation amount (default: 0)
 * @param {number} windSpeed - Wind speed in km/h (default: 0)
 * @param {boolean} includeBackground - Whether to include background colors for UI (default: false)
 * @returns {object} Recommendation object with description, layers, emojis, and optional background
 */
export const getLayerRecommendation = (temperature, precipitation = 0, windSpeed = 0, includeBackground = false) => {
  let recommendation = { description: '', layers: 0, emojis: [] };

  // Core temperature-based logic using custom instructions
  if (temperature > 20) {
    recommendation = { description: 'T-shirt weather', layers: 1, emojis: ['👕'] };
  } else if (temperature > 17) {
    recommendation = { description: 'T-shirt weather', layers: 1, emojis: ['👕'] };
  } else if (temperature >= 15 && temperature <= 17) {
    if (windSpeed > 20) {
      recommendation = { description: 'Light jacket weather - windy', layers: 2, emojis: ['👕', '🧥'] };
    } else {
      recommendation = { description: 'T-shirt weather', layers: 1, emojis: ['👕'] };
    }
  } else if (temperature > 7 && temperature <= 15) {
    recommendation = { description: 'Light jacket weather', layers: 2, emojis: ['👕', '🧥'] };
  } else if (temperature > -5 && temperature <= 7) {
    recommendation = { description: 'Sweater and jacket weather', layers: 3, emojis: ['👕', '🧥', '🧣'] };
  } else if (temperature >= -17 && temperature <= -5) {
    recommendation = { description: 'Winter jacket weather', layers: 4, emojis: ['👕', '🧥', '🧣', '👞'] };
  } else { // temperature < -17
    recommendation = { description: 'Full winter gear weather', layers: 5, emojis: ['👕', '🧥', '🧣', '🧤', '👞'] };
  }

  // Add precipitation emoji if there's rain or snow
  if (precipitation > 0) {
    recommendation.emojis.push('☂️');
  }

  // Add wind emoji consistently if windy
  if (windSpeed > 20) {
    recommendation.emojis.push('🌬️');
  }

  // Add background colors only for frontend UI
  if (includeBackground) {
    if (temperature <= -17) {
      recommendation.background = 'bg-purple-50 dark:bg-purple-900';
    } else if (temperature > 17) {
      recommendation.background = 'bg-yellow-50 dark:bg-yellow-900';
    } else if (temperature >= 15 && temperature <= 17 && windSpeed > 10) {
      recommendation.background = 'bg-blue-100 dark:bg-blue-900';
    } else if (temperature >= 15 && temperature <= 17) {
      recommendation.background = 'bg-yellow-50 dark:bg-yellow-900';
    } else if (temperature > 7 && temperature <= 15) {
      recommendation.background = 'bg-blue-50 dark:bg-blue-900';
    } else if (temperature > -5 && temperature <= 7) {
      recommendation.background = 'bg-indigo-50 dark:bg-indigo-900';
    } else {
      recommendation.background = 'bg-purple-50 dark:bg-purple-900';
    }

    if (precipitation > 0) {
      recommendation.background += ' bg-opacity-75 dark:bg-opacity-75';
    }
  }

  return recommendation;
};

/**
 * Validate temperature range
 * @param {number} temperature - Temperature in Celsius
 * @returns {boolean} Whether temperature is in valid range
 */
export const isValidTemperature = (temperature) => {
  return !isNaN(temperature) && temperature >= -50 && temperature <= 50;
};

/**
 * Convert Fahrenheit to Celsius
 * @param {number} fahrenheit - Temperature in Fahrenheit
 * @returns {number} Temperature in Celsius
 */
export const convertToCelsius = (fahrenheit) => {
  return (fahrenheit - 32) * 5 / 9;
};

/**
 * Convert Celsius to Fahrenheit
 * @param {number} celsius - Temperature in Celsius
 * @returns {number} Temperature in Fahrenheit
 */
export const convertToFahrenheit = (celsius) => {
  return (celsius * 9 / 5) + 32;
};